import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Autocomplete, SmallInputLabel, TeamAutocomplete, UserAutocomplete } from "src/components";
import { useAddUsersToTeam, useGetTeamNetwork, type UserDto } from "src/http";
import type { ITeamNetworkRole, ITeamSummary } from "src/models";

interface IAddTeamMemberFormState {
  team: ITeamSummary | null;
  users: UserDto[];
  role: ITeamNetworkRole | null;
}

interface IProps {
  visible: boolean;
  initialTeam?: ITeamSummary;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddTeamMemberModal = (props: IProps) => {
  const { mutate, isLoading: isAddingMembers } = useAddUsersToTeam();

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm<IAddTeamMemberFormState>({
    defaultValues: {
      team: null,
      users: [],
      role: null,
    },
  });

  const selectedTeam = watch("team");
  const { data: teamNetwork, isLoading: isLoadingRoles } = useGetTeamNetwork(selectedTeam?.slug);

  useEffect(() => {
    if (props.visible && props.initialTeam && !selectedTeam) {
      setValue("team", props.initialTeam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible, props.initialTeam, setValue]);

  useEffect(() => {
    setValue("role", null);
  }, [selectedTeam, setValue]);

  const availableRoles = useMemo(() => {
    if (!teamNetwork?.roles || !selectedTeam) {
      return [];
    }
    return [...teamNetwork.roles];
  }, [teamNetwork?.roles, selectedTeam]);

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    handleCancelClicked();
  }

  async function handleDoneClicked() {
    handleSubmit((data) => {
      if (!data.team) {
        return;
      }

      mutate(
        {
          teamSlug: data.team.slug,
          roleGuid: data.role?.guid,
          userGuids: data.users.map((u) => u.userId),
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onSuccess();
          },
        },
      );
    })();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancel();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Team Member</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Stack spacing={0.25}>
            <SmallInputLabel>Team *</SmallInputLabel>
            <Controller
              name="team"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Team is required",
                },
              }}
              render={({ field }) => (
                <TeamAutocomplete
                  placeholder="Select a team..."
                  value={field.value}
                  onChange={field.onChange}
                  groupByTeamMembership
                  disableOptionsBasedOnMembership
                  error={errors.team !== undefined}
                  helperText={errors.team?.message}
                />
              )}
            />
          </Stack>

          <Stack spacing={0.25}>
            <SmallInputLabel>Users *</SmallInputLabel>
            <Controller
              name="users"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "At least one user is required",
                },
                validate: (value) => {
                  return value && value.length > 0 ? true : "At least one user is required";
                },
              }}
              render={({ field }) => (
                <UserAutocomplete
                  placeholder="Select one or more users..."
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.users !== undefined}
                  helperText={errors.users?.message}
                  multiple
                />
              )}
            />
          </Stack>

          <Stack spacing={0.25}>
            <SmallInputLabel>Role</SmallInputLabel>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingRoles}
                  disabled={isLoadingRoles || !selectedTeam || availableRoles.length === 0}
                  options={availableRoles}
                  getOptionLabel={(option) => option.name}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  renderOption={(props, option) => (
                    <Stack key={option.guid} sx={{ width: "100%" }}>
                      <MenuItem {...props} component="li" sx={{ py: 1 }}>
                        <Stack>{option.name}</Stack>
                      </MenuItem>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={!selectedTeam ? "Select a team first..." : "Select a role (optional)..."}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.guid === value?.guid}
                />
              )}
            />
            <Typography variant="caption" color="text.primary">
              If no role is selected, a generic <strong>Team Member</strong> role will be created for the user(s).
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isAddingMembers} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isAddingMembers}
          endIcon={isAddingMembers ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleDoneClicked}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddTeamMemberModal };
